import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  arrowHeadLeftSVG,
  shopSVG,
  switchShopSVG,
  ToggleSideBarSVG,
  userSVG,
} from '../../lib/allCustomSVGs';
import '../../styles/components/sidebar/sidebarTara.scss';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import store, { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  linksDashboard,
  linksAgents,
  linksRecommendations,
  linksSettings,
  linksTracking,
  // Action Center — a single top-level entry. The queue is the landing page;
  // History / Auto-Approval Rules / Approval Settings are reached via the
  // in-page tab strip on the Action Center page (no longer a nested menu).
  linksActionCenter,
  // Phase 13 — new top-level groupings (Brands nested + Team + MCP setup).
  // Note: `linksReports` is intentionally NOT imported any more — the
  // Reports section was removed from the sidebar nav, and its `/reports/*`
  // routes are switched off in route.ts on every environment.
  linksBrands,
  linksTeam,
  linksMcpSetup,
  // Phase 13 — top-level Stats menu entry (page still mounted at both
  // `/stats` and `/agency/stats` — see App.tsx).
  linksStats,
  // Unified Log Center menu entry (page at `/logs`) — hosts the Audit +
  // Meta API activity tabs. Replaces the old separate Audit log / Meta
  // activity entries; those routes redirect into the Log Center.
  linksLogCenter,
  // The single "Integrations" entry — the unified hub at /integrations
  // (platforms + MCP servers/governance + tool access as tabs).
  linksIntegrations,
  // P3 — Client-report portal (agency review/edit/approve surface).
  linksClientReports,
} from '../../lib/constants/sidebarLinks';
import { allRoutes } from '../../lib/constants/route';
import {
  canResolve,
  canViewLogCenter as resolveLogCenterAccess,
  canViewStatsPage,
  canViewTeamPage,
  useCapabilities,
  useEmployeeRole,
} from '../../lib/rbac';
import SidebarProfileTheme2 from './SidebarProfileTheme2';
import BrandQuickSwitch from './BrandQuickSwitch';
import truncate from '../../lib/helper-functions/truncate';
import {
  setIsPaymentPopupOpen,
  setIsSwitchShopPopupOpen,
} from '../../store/reducers/uiSlice';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { isAgencyOnlySession } from '../../lib/api/agency-shell-init';
import { usePlanLock } from '../../lib/plan-lock/usePlanLock';
import { LOCKED_SETTINGS_LINKS } from '../../lib/plan-lock/plan-lock-rules';
// 2026-09-07 — the official AdUp wordmark ("AdUp.") as the sidebar logo
// fallback (the CDN `default.jpg` was a generic grey "picture" placeholder).
// These are the source wordmark PNGs cropped to the mark itself — the originals
// are ~80% transparent padding and rendered tiny in the slot.
import adupWordmarkDark from '../../assets/adup-wordmark-dark.png';
import adupWordmarkLight from '../../assets/adup-wordmark-light.png';

interface Props {
  isSidebarMinimized: boolean;
  changeSidebarSize: () => void;
}

interface SidebarLink {
  title: string;
  link: string;
  icon: any;
  iconType: string;
  specialURL?: string;
  urlName?: string;
}

interface MenuItemProps {
  item: SidebarLink;
  currentPath: string;
  isSidebarMinimized: boolean;
  isNested: boolean;
  navigate?: (path: string) => void;
  onClick?: () => void;
  isModal?: boolean;
}

interface NestedMenuItemProps extends Omit<MenuItemProps, 'item' | 'isNested'> {
  isOpen: boolean;
  onToggle: () => void;
  icon: any;
  title: string;
  children?: SidebarLink[];
  route: keyof typeof allRoutes;
}

// Segment-aware "is the current path inside this section" test. Matching is by
// PATH SEGMENT, never a loose substring: the path must equal the section root
// or sit directly beneath it (`root` OR `root/…`). An empty/absent section
// root never matches — the previous `currentPath.includes('')` returned true
// for every route, which permanently highlighted any entry without a urlName
// (e.g. the footer brand-switch item).
const isPathUnderSection = (currentPath: string, section: string): boolean =>
  !!section &&
  (currentPath === section || currentPath.startsWith(`${section}/`));

// Resolves the `data-sub_page_link_holder` value for a sidebar link, always
// returning the string 'true'/'false' the CSS hook keys off (the branches used
// to mix raw booleans and strings).
const getSidebarItemActiveState = (
  item: SidebarLink,
  currentPath: string,
  search: string,
  isModal?: boolean
): 'true' | 'false' => {
  if (isModal) return 'false';

  // A `specialURL` that carries a query string (a deep-linked tab, e.g. the
  // Log Center tabs) matches on pathname+search so only the active tab lights
  // up; a bare `specialURL` matches its path segment.
  if (item.specialURL) {
    if (item.specialURL.includes('?')) {
      return `${currentPath}${search}` === item.specialURL ? 'true' : 'false';
    }
    return isPathUnderSection(currentPath, item.specialURL) ? 'true' : 'false';
  }

  // `urlName` is the section-root segment (e.g. 'actions' → '/actions/*',
  // 'team' → '/team'); it keeps the entry highlighted across every sub-route.
  return isPathUnderSection(currentPath, item.urlName ? `/${item.urlName}` : '')
    ? 'true'
    : 'false';
};

const SidebarTara = ({ isSidebarMinimized, changeSidebarSize }: Props) => {
  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const taraTrialDetails = useSelector(
    (state: RootState) => state.user.shop.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };
  const staticURL = useSelector((state: RootState) => state.app.staticURL);
  const merchantTheme = useSelector(
    (state: RootState) => state.user.merchant.config
  );
  const isTrackingScriptConnectionSuccess = useSelector(
    (state: RootState) =>
      state.user?.isTrackingScriptConnectionSuccess ?? undefined
  );
  const isTaraChatAnimationEnabled = useSelector(
    (state: RootState) => state.UI.isTaraChatAnimationEnabled
  );
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { t } = useTranslation();
  // Phase 12 — RBAC-gated sidebar links. Today `/team`, `/stats`,
  // `/integrations` and `/settings/roles` flow through this; extend the map as
  // more links gain capability gates.
  const employeeRole = useEmployeeRole();
  const employeeIdentity = useSelector(
    (state: RootState) => state.auth?.agency?.employeeIdentity
  );
  const sellerTokenForNav = useSelector(
    (state: RootState) => state.auth?.authorization?.accessToken?.sellerToken
  );
  const agencyAccessTokenForNav = useSelector(
    (state: RootState) => state.auth?.agency?.accessToken
  );
  const isWhiteLabelForNav = useSelector(
    (state: RootState) => state.UI?.isWhiteLabel ?? false
  );
  // Phase 1 (custom roles) — gate the nav off the acting employee's LIVE
  // `/me.capabilities` when present (falling back to the static role matrix
  // otherwise). A local `can()` bound to that list keeps every existing call
  // site below unchanged while making role edits reflect in the menu.
  const liveCapabilities = useCapabilities();
  // No active plan or trial: the menu shrinks to Subscription and Profile.
  // Help and sign-out stay in the profile menu at the bottom.
  const { locked: isPlanLocked } = usePlanLock();
  const can = (
    role: typeof employeeRole,
    cap: Parameters<typeof canResolve>[2]
  ) => canResolve(liveCapabilities, role, cap);
  const linkCapabilityGuards: Record<string, boolean> = {
    // The same check the Team page makes (`canViewTeamPage`).
    '/team': canViewTeamPage(liveCapabilities, employeeRole),
    // Phase 1 (custom roles) — owner-only Roles management page. Gated on
    // `settings.manage_org` (the org-admin capability); the page-level guard in
    // SettingsPage uses the same check, so a non-owner can neither see the link
    // nor reach the route directly.
    '/settings/roles': can(employeeRole, 'settings.manage_org'),
    // The Stats entry follows the same check as the page itself
    // (`pages/stats/Stats.tsx`), so the menu never links to a page that
    // refuses the member.
    '/stats': canViewStatsPage(liveCapabilities, employeeRole),
    // W3 — Control Center mutations gate on `integrations.manage`
    // (owner/team_lead/manager); the page itself is browsable read-only by
    // everyone else, so the NAV entry keys on `integrations.view` (all roles).
    // It used to key on `integrations.manage`, which hid the hub from
    // analyst/read_only entirely — they could neither reach it from the menu
    // nor load it if they deep-linked, because the backend read routes were
    // gated on the manage role set too. Both halves now split view from manage.
    // The hub lives at /integrations, so the guard is keyed on that link (the
    // guard lookup is by `item.link`).
    '/integrations': can(employeeRole, 'integrations.view'),
    // Reporting portal review/edit/approve surface — now a SINGLE sidebar
    // entry (targets / branding / settings are in-page tabs, so they no longer
    // need their own guards here). analyst+ can review; the page re-gates.
    // Hidden for read_only.
    '/client-reports': can(employeeRole, 'portal.reports.review'),
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Compact brand quick-switch popover anchored on the bottom-left button.
  const [isBrandSwitchOpen, setIsBrandSwitchOpen] = useState(false);
  const [openedNestedMenuLinks, setOpenedNestedMenuLinks] = useState<string[]>([
    currentPath.split('/')[1],
  ]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const brandSwitchRef = useRef<HTMLDivElement>(null);

  const activeShopName = userProfile?.all_shops?.find(
    (shop: any) => shop?.slug === userProfile?.active_shop_slug
  )?.name;

  // Toggle dropdown open/close state
  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Effect to handle click outside to close the user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Click-outside + Escape to dismiss the compact brand quick-switch popover.
  // Mirrors the profile-dropdown pattern (the ref wraps both the trigger button
  // and the popover, so clicking the button itself doesn't re-close it).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        brandSwitchRef.current &&
        !brandSwitchRef.current.contains(event.target as Node)
      ) {
        setIsBrandSwitchOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsBrandSwitchOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const nestedMenuLink = currentPath.split('/')[1];
    setOpenedNestedMenuLinks([...openedNestedMenuLinks, nestedMenuLink]);
  }, [currentPath]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPath]);

  const MenuItem = ({
    item,
    currentPath,
    isSidebarMinimized,
    isNested,
    navigate,
    onClick,
    isModal,
  }: MenuItemProps) => {
    if (!allRoutes[item.link as keyof typeof allRoutes] && !isModal) {
      return null;
    }
    if (
      item.link in linkCapabilityGuards &&
      !linkCapabilityGuards[item.link]
    ) {
      return null;
    }

    return (
      <motion.li
        title={t(item.title)}
        initial={{ opacity: 0.8 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0.8 }}
        transition={{ duration: 0.1, ease: 'backInOut' }}
        onClick={() => {
          if (onClick) {
            onClick();
          } else {
            // Honour `specialURL` (e.g. a deep-linked tab query string) when
            // present so entries like Tool Access land on the right tab.
            navigate && navigate(item.specialURL || item.link);
          }
        }}
        data-sub_page_link_holder={getSidebarItemActiveState(
          item,
          currentPath,
          location.search,
          isModal
        )}
        data-is_nested_link_holder={isNested ? 'true' : 'false'}
        className='sidebarTara__bottom--linkItem'
      >
        <div className='sidebarTara__bottom--linkItem-left'>
          <span className='sidebarTara__bottom--linkItem-left-highlighter'></span>
          {item.iconType === 'svg' ? (
            <CustomSVGs svg={item.icon} />
          ) : (
            <img
              src={item.icon}
              className='subPageLinkHolderItems__img'
              alt='icon'
            />
          )}
          <motion.span
            initial={{ opacity: 0.8 }}
            animate={{ opacity: isSidebarMinimized ? 0 : 1 }}
            transition={{ duration: 0.1 }}
          >
            {t(item.title)}
          </motion.span>
        </div>
      </motion.li>
    );
  };

  const NestedMenuItem = ({
    isOpen,
    onToggle,
    icon,
    title,
    children,
    currentPath,
    isSidebarMinimized,
    navigate,
    route,
  }: NestedMenuItemProps) => {
    return (
      <>
        <motion.li
          title={t(title)}
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0.8 }}
          transition={{ duration: 0.1, ease: 'backInOut' }}
          onClick={onToggle}
          key={title}
          className='sidebarTara__bottom--linkItem'
          data-sub_page_link_holder={
            currentPath?.startsWith(route) && !isOpen ? 'true' : 'false'
          }
          data-is-nested-link-open={isOpen ? 'true' : 'false'}
        >
          <div className='sidebarTara__bottom--linkItem-left'>
            <span className='sidebarTara__bottom--linkItem-left-highlighter'></span>
            <CustomSVGs svg={icon} />
            <motion.span
              initial={{ opacity: 0.8 }}
              animate={{ opacity: isSidebarMinimized ? 0 : 1 }}
              transition={{ duration: 0.1 }}
            >
              {t(title)}
            </motion.span>
          </div>
          <div className='sidebarTara__bottom--linkItem-right'>
            <CustomSVGs svg={arrowHeadLeftSVG} />
          </div>
        </motion.li>
        {isOpen &&
          children?.map((item, i) => (
            <MenuItem
              key={i}
              item={item}
              currentPath={currentPath}
              isSidebarMinimized={isSidebarMinimized}
              isNested={true}
              navigate={navigate}
            />
          ))}
      </>
    );
  };

  const renderSectionView = (
    links: SidebarLink[],
    route: keyof typeof allRoutes,
    options?: {
      isNested?: boolean;
      icon?: any;
      title?: string;
    }
  ) => {
    if (!allRoutes[route]) return null;

    if (options?.isNested) {
      const routeName = route.replace('/', '');
      return (
        <AnimatePresence>
          <NestedMenuItem
            isOpen={openedNestedMenuLinks.includes(routeName)}
            onToggle={() => {
              if (openedNestedMenuLinks.includes(routeName)) {
                setOpenedNestedMenuLinks(
                  openedNestedMenuLinks.filter((item) => item !== routeName)
                );
              } else {
                setOpenedNestedMenuLinks([...openedNestedMenuLinks, routeName]);
              }
            }}
            icon={options.icon}
            title={options.title || ''}
            children={links}
            currentPath={currentPath}
            isSidebarMinimized={isSidebarMinimized}
            navigate={navigate}
            route={route}
          />
        </AnimatePresence>
      );
    }

    return (
      <AnimatePresence>
        {links?.map((item, i) => (
          <MenuItem
            key={i}
            item={item}
            currentPath={currentPath}
            isSidebarMinimized={isSidebarMinimized}
            isNested={false}
            navigate={navigate}
          />
        ))}
      </AnimatePresence>
    );
  };

  /* --------------------------- Main Menu ---------------------------- */
  // Phase 13 — the sidebar no longer exposes "Integrations", "Prompt
  // Schedules" or the LIFTIQ "AI Optimizations" external link as top-level
  // entries. The pages remain reachable by direct URL; only nav was
  // pruned. See `linksBrands` for the nested replacement that groups the
  // brand switcher (/clients) with Integrations.
  const dashboardSectionView = () =>
    renderSectionView(linksDashboard, '/dashboard');
  // Phase 13 — Stats sits between Dashboard and Brands in the main menu.
  // The entry and the page share one check (`canViewStatsPage`), so the menu
  // never links to a "no access" page.
  const canViewStats = canViewStatsPage(liveCapabilities, employeeRole);
  const statsSectionView = () => renderSectionView(linksStats, '/stats');
  // Log Center sits right after Stats. It hosts the Audit, Meta API and MCP
  // calls tabs, so the link shows when the member can view ANY of them (the
  // page renders only the tabs their capabilities allow). Same check as the
  // page, so the menu never links to a "no access" message.
  const canViewLogCenter = resolveLogCenterAccess(
    liveCapabilities,
    employeeRole
  );
  const logCenterSectionView = () =>
    renderSectionView(linksLogCenter, '/logs');
  const brandsSectionView = () =>
    renderSectionView(linksBrands, '/clients', {
      isNested: true,
      icon: shopSVG,
      title: 'Brands',
    });
  // The unified Integrations hub at /integrations (platforms + MCP servers +
  // tool access as tabs). Visible to EVERY role via `integrations.view`; the
  // page renders read-only without `integrations.manage`, and the Tool Access /
  // GDPR tabs keep their own `mcp.access.view` gate inside the page.
  const integrationsSectionView = () =>
    renderSectionView(linksIntegrations, '/integrations');
  const agentsSectionView = () => renderSectionView(linksAgents, '/agents');
  const teamSectionView = () => renderSectionView(linksTeam, '/team');
  const mcpSetupSectionView = () =>
    renderSectionView(linksMcpSetup, '/my-mcp-setup');
  // Phase 13 — Reports section removed from the sidebar nav. The `/reports/*`
  // routes are also switched off in route.ts on every environment, so a direct
  // URL lands on the 404 page. Client reports live under /client-reports.
  // Action Center — single top-level entry (the old 4-item nested Actions menu
  // is gone). It lands on the queue; the other Actions surfaces (History,
  // Auto-Approval Rules, Approval Settings) are tabs on the Action Center page.
  const actionCenterSectionView = () =>
    renderSectionView(linksActionCenter, '/actions');
  // P3 — Client-report portal (review/edit/approve). Gated on
  // `portal.reports.review` (analyst+) both here and in the guard map.
  const clientReportsSectionView = () =>
    renderSectionView(linksClientReports, '/client-reports');
  const recommendationsSectionView = () =>
    renderSectionView(linksRecommendations, '/recommendations');

  /* --------------------------- Tracking Section ---------------------------- */
  const trackingSectionView = () =>
    renderSectionView(linksTracking, '/tracking');

  /* --------------------------- Settings Section ---------------------------- */
  // 2026-09-07 — Communication / Subscription / API Keys are seller-account
  // pages (seller JWT). For an agency-only session only the employee page
  // (served at General + Profile) applies, so hide the rest of the sub-nav.
  const settingsLinksForSession = isPlanLocked
    ? linksSettings.filter((l) => LOCKED_SETTINGS_LINKS.includes(l.link))
    : isAgencyOnlySession(
        agencyAccessTokenForNav,
        sellerTokenForNav,
        isWhiteLabelForNav
      )
    ? linksSettings.filter((l) =>
        ['/settings/general', '/settings/profile'].includes(l.link)
      )
    : linksSettings;
  const settingsSectionView = () =>
    renderSectionView(settingsLinksForSession, '/settings');

  /* --------------------------- Render Logo ---------------------------- */
  const RenderLogo = () => {
    if (
      merchantTheme.merchant_logo_light &&
      merchantTheme.merchant_logo_dark &&
      merchantTheme.merchant_name === 'Buckaroo'
    ) {
      return (
        <img
          src={`${staticURL}/medium/${
            isDarkMode
              ? merchantTheme.merchant_logo_light
              : merchantTheme.merchant_logo_dark
          }`}
          alt='logo'
        />
      );
    } else if (
      merchantTheme.merchant_logo_dark &&
      merchantTheme.merchant_logo_light
    ) {
      return (
        <img
          src={`${staticURL}/medium/${
            isDarkMode
              ? merchantTheme.merchant_logo_light
              : merchantTheme.merchant_logo_dark
          }`}
          alt='logo'
        />
      );
    } else {
      // No merchant logo pair (every invite-only employee session, and any
      // seller whose merchant record carries none): show the ADUP wordmark
      // instead of the CDN's stock placeholder. Bundled asset — no dependency
      // on `staticURL`, so it also renders before/without seller-init.
      return (
        <img
          className='sidebarTara__logo--fallback'
          src={isDarkMode ? adupWordmarkLight : adupWordmarkDark}
          alt='AdUp'
        />
      );
    }
  };

  /* --------------------------- Get Profile Name ---------------------------- */
  // 2026-09-07 — `user.profile` (first/last name, email) is filled by
  // seller-init only. An invite-only employee has none of it, so the footer
  // read the literal "User". Fall back to the employee identity from /me.
  const getProfileName = () => {
    if (userProfile?.first_name || userProfile?.last_name) {
      return `${userProfile?.first_name} ${userProfile?.last_name}`;
    } else if (userProfile?.email) {
      return `${userProfile.email}`;
    } else if (employeeIdentity?.name) {
      return employeeIdentity.name;
    } else if (employeeIdentity?.email) {
      return employeeIdentity.email;
    } else {
      return t('User');
    }
  };

  const renderSideBar = () => {
    return (
      <ul className='sidebarTara__bottom'>
        <div className='sidebarTara__bottom--links'>
          {/* Main Menu — Phase 13 ordering. Brands (nested),
              Actions (nested), Team (gated, top-level /team),
              Agents, Recommendations, My MCP Setup. Reports has been removed
              from the nav (still reachable via direct URL). */}
          {!isPlanLocked &&
            !currentPath?.startsWith('/settings') &&
            !currentPath?.startsWith('/tracking') && (
              <>
                {isTrackingScriptConnectionSuccess === 'true' &&
                  dashboardSectionView()}
                {canViewStats && statsSectionView()}
                {canViewLogCenter && logCenterSectionView()}
                {brandsSectionView()}
                {/* Single canonical hub entry: "Integrations" at
                    /integrations (every platform/MCP OAuth round-trips back
                    there). The old separate Control Center + Tool Access
                    entries are gone — both surfaces are tabs on the hub. */}
                {can(employeeRole, 'integrations.view') &&
                  integrationsSectionView()}
                {actionCenterSectionView()}
                {can(employeeRole, 'portal.reports.review') &&
                  clientReportsSectionView()}
                {can(employeeRole, 'team.view') && teamSectionView()}
                {agentsSectionView()}
                {recommendationsSectionView()}
                {mcpSetupSectionView()}
              </>
            )}

          {/* Tracking Section */}
          {!isPlanLocked && currentPath?.startsWith('/tracking') && (
            <>{trackingSectionView()}</>
          )}

          {/* Settings Section */}
          {(isPlanLocked || currentPath?.startsWith('/settings')) && (
            <>{settingsSectionView()}</>
          )}
        </div>
      </ul>
    );
  };

  return (
    <div
      data-is-sidebar-minimized={isSidebarMinimized?.toString()}
      className='sidebarTara'
    >
      <div className='sidebarTara__top'>
        <div className='sidebarTara__top--left'>{RenderLogo()}</div>

        <button
          type='button'
          onClick={changeSidebarSize}
          aria-label={
            isSidebarMinimized ? 'Expand sidebar' : 'Collapse sidebar'
          }
          className='sidebarTara__top--right'
        >
          <CustomSVGs svg={ToggleSideBarSVG} />
        </button>
      </div>
      {renderSideBar()}

      <div
        data-is-sidebar-profile-open={isDropdownOpen.toString()}
        className='sidebarTara__profile'
      >
        {/* Trial End Banner */}
        {!isPlanLocked &&
          taraTrialDetails &&
          taraTrialDetails?.is_trial &&
          taraTrialDetails?.trial_available_days &&
          taraTrialDetails?.trial_available_days > 0 && (
            <div
              className='sidebarTara__banner'
              onClick={() => {
                store.dispatch(setIsPaymentPopupOpen(true));
              }}
            >
              <div className='sidebarTara__banner--left'>
                <h3>
                  {isSidebarMinimized
                    ? '⏱︎ ' + taraTrialDetails?.trial_available_days
                    : t('TrialEndsInDays', {
                        days: taraTrialDetails?.trial_available_days,
                      })}
                </h3>
              </div>
            </div>
          )}

        {/* Brand quick-switch — the bottom-left button now TOGGLES a compact
            popover (search + brand list + "See all") anchored here, instead of
            navigating straight to /clients. The full SwitchShop popup and its
            other triggers are untouched. */}
        {!isPlanLocked &&
          userProfile &&
          userProfile?.role === 'agency' &&
          userProfile?.all_shops &&
          Array.isArray(userProfile?.all_shops) &&
          userProfile?.all_shops?.length > 0 && (
            <div
              className='sidebarTara__brand-switch'
              ref={brandSwitchRef}
            >
              <AnimatePresence>
                <MenuItem
                  key={'switch-shop'}
                  item={{
                    icon: switchShopSVG,
                    iconType: 'svg',
                    title:
                      activeShopName && isNotEmptyString(activeShopName)
                        ? truncate(activeShopName, 17)
                        : t('SwitchShop'),
                    link: '/clients',
                  }}
                  currentPath={currentPath}
                  isSidebarMinimized={isSidebarMinimized}
                  isNested={false}
                  isModal={false}
                  onClick={() => {
                    setIsBrandSwitchOpen((prev) => !prev);
                  }}
                />
              </AnimatePresence>
              <AnimatePresence>
                {isBrandSwitchOpen && (
                  <BrandQuickSwitch
                    onClose={() => setIsBrandSwitchOpen(false)}
                  />
                )}
              </AnimatePresence>
            </div>
          )}

        {/* Profile Dropdown */}
        <div
          className='sidebarTara__profile--content'
          onClick={toggleDropdown}
          ref={dropdownRef}
        >
          <div className='sidebarTara__profile--content-left'>
            <CustomSVGs svg={userSVG} />
            <span title={getProfileName()}>
              {truncate(getProfileName(), 17)}
            </span>
          </div>
          <div className='sidebarTara__profile--content-right'>
            <CustomSVGs svg={arrowHeadLeftSVG} />
          </div>
          <AnimatePresence>
            {isDropdownOpen && <SidebarProfileTheme2 />}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SidebarTara;
